import React from "react";
import { Link } from "react-router-dom";
import { Quote, Trophy, MapPin, ArrowRight, BadgeCheck } from "lucide-react";
import { optimg } from "../lib/optimg";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const isPhoto = (url) => !!url && !/brochure_page/i.test(url);
const absolute = (url) => (url && url.startsWith("/api/") ? `${API_URL}${url}` : url);

export const TopperSpotlight = ({ topper }) => {
  if (!topper) return null;
  const photo = absolute(isPhoto(topper.photo_url) ? topper.photo_url : isPhoto(topper.image_url) ? topper.image_url : null);
  const initials = (topper.name || "").split(" ").map((w) => w[0]).slice(0, 2).join("");
  return (
    <section
      data-testid="topper-spotlight"
      className="relative overflow-hidden rounded-[28px] bg-[#071E54] text-white shadow-2xl border border-white/10"
    >
      <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-amber-400/20 blur-3xl" />
      <div className="absolute -bottom-24 -left-20 w-72 h-72 rounded-full bg-[#003399]/60 blur-3xl" />
      <div className="relative grid md:grid-cols-[300px_1fr] gap-8 p-6 sm:p-10 items-center">
        <div className="relative mx-auto md:mx-0">
          <div className="w-56 h-64 sm:w-64 sm:h-72 rounded-3xl overflow-hidden ring-4 ring-amber-400 shadow-[0_20px_60px_-15px_rgba(255,193,7,0.6)] bg-gradient-to-br from-[#0D47A1] to-[#071E54] flex items-center justify-center">
            {photo ? (
              <img src={optimg(photo, 640)} alt={topper.name} width="256" height="288" loading="lazy" decoding="async" className="w-full h-full object-cover" data-testid="topper-spotlight-photo" />
            ) : (
              <div className="text-center px-4" data-testid="topper-spotlight-monogram">
                <div className="text-6xl font-black text-amber-300 tracking-tight">{initials}</div>
                <div className="mt-2 text-[10px] uppercase tracking-[0.25em] text-blue-200 font-black">Ranker · Batch of 2026</div>
              </div>
            )}
          </div>
          <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full bg-amber-400 text-[#071E54] text-xs font-black shadow-lg flex items-center gap-1.5 whitespace-nowrap">
            <Trophy className="w-3.5 h-3.5" /> JEE Advanced 2026
          </div>
        </div>

        <div className="space-y-4 text-center md:text-left">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 border border-white/15 text-[10px] font-black uppercase tracking-[0.2em] text-amber-300">
            <BadgeCheck className="w-3.5 h-3.5" /> Topper Spotlight
          </span>
          <h2 data-testid="topper-spotlight-name" className="text-3xl sm:text-5xl font-black tracking-tight leading-none">{topper.name}</h2>
          <p className="text-base md:text-lg font-bold text-blue-100 flex items-center justify-center md:justify-start gap-2">
            <MapPin className="w-4 h-4 text-amber-300" /> Selected for <span className="text-white">{topper.college}</span>
            {topper.score_or_percentile ? <span className="text-blue-300 font-semibold">· {topper.score_or_percentile}</span> : null}
          </p>
          {topper.quote && (
            <blockquote data-testid="topper-spotlight-quote" className="relative pl-6 text-sm sm:text-base text-blue-50 italic leading-relaxed max-w-2xl mx-auto md:mx-0">
              <Quote className="w-5 h-5 text-amber-400 absolute left-0 -top-1" />
              “{topper.quote}”
            </blockquote>
          )}
          <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 pt-1">
            {topper.tag && <span className="px-3 py-1 rounded-full bg-white/10 text-xs font-bold text-blue-100">{topper.tag}</span>}
            <span className="px-3 py-1 rounded-full bg-white/10 text-xs font-bold text-blue-100">Laxmi Nagar, Nagpur</span>
            <Link to="/demo" data-testid="topper-spotlight-cta" className="ml-0 md:ml-2 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-amber-400 hover:bg-amber-300 text-[#071E54] text-xs font-black transition-colors">
              Start your journey <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};
